import { lazy } from 'react';
import { IntlProvider } from 'react-intl';
import { Route, Routes } from 'react-router-dom';
import { SessionExpiredDialog } from '@rio-cloud/rio-session-expired-info';
import ApplicationLayout from '@rio-cloud/rio-uikit/ApplicationLayout';
import NotificationsContainer from '@rio-cloud/rio-uikit/NotificationsContainer';

import { DEFAULT_LOCALE } from '../configuration/lang/lang';
import { isUserSessionExpired } from '../configuration/login/loginSlice';
import { useAppDispatch, useAppSelector } from '../configuration/setup/hooks';
import { getDisplayMessages, getLocale } from '../configuration/lang/langSlice';
import {
    DEFAULT_ROUTE,
    INCOMING_ROUTE,
    OUTGOING_ROUTE,
    CREATE_ROUTE,
    CARRIER_COMPLIANCE_ROUTE,
} from '../routes/routes';
import DefaultRedirect from '../routes/DefaultRedirect';
import RouteUpdater from '../routes/RouteUpdater';
import { getSessionExpiredAcknowledged, hideSessionExpiredDialog } from './appSlice';
import AppHeader from '../features/header/AppHeader';
import SuspendedWithSpinner from '../components/SuspendedWithSpinner';
import CarrierCompliance from '../pages/CarrierCompliance';

// Lazy load pages for better performance and automatically split the bundle accordingly
const StartPage = lazy(() => import('../pages/StartPage'));
const IncomingOrders = lazy(() => import('../pages/IncomingOrders'));
const OutgoingOrders = lazy(() => import('../pages/OutgoingOrders'));
const CreateOrder = lazy(() => import('../pages/CreateOrder'));
const OrderDetailsPage = lazy(() => import('../pages/OrderDetailsPage'));

const App = () => {
    const dispatch = useAppDispatch();

    const userLocale = useAppSelector(getLocale);
    const displayMessages = useAppSelector(getDisplayMessages);
    const isSessionExpired = useAppSelector(isUserSessionExpired);
    const sessionExpiredAcknowledged = useAppSelector(getSessionExpiredAcknowledged);

    if (!(displayMessages && userLocale)) {
        return null;
    }

    const handleSessionExpiredDialogClose = () => dispatch(hideSessionExpiredDialog);
    const showSessionExpired = isSessionExpired && !sessionExpiredAcknowledged;

    return (
        <IntlProvider defaultLocale={DEFAULT_LOCALE} key={userLocale} locale={userLocale} messages={displayMessages}>
            <ApplicationLayout className={'TransportOrders'}>
                <ApplicationLayout.Header>
                    <AppHeader />
                </ApplicationLayout.Header>
                <ApplicationLayout.Sidebar className='right' />
                <ApplicationLayout.Body>
                    <NotificationsContainer />
                    <SessionExpiredDialog
                        locale={userLocale}
                        onClose={handleSessionExpiredDialogClose}
                        show={showSessionExpired}
                    />
                    <Routes>
                        <Route
                            path={`${DEFAULT_ROUTE}/*`}
                            element={
                                <SuspendedWithSpinner>
                                    <StartPage />
                                </SuspendedWithSpinner>
                            }
                        />
                        <Route
                            path={`${INCOMING_ROUTE}/*`}
                            element={
                                <SuspendedWithSpinner>
                                    <IncomingOrders />
                                </SuspendedWithSpinner>
                            }
                        />
                        <Route
                            path={`${INCOMING_ROUTE}/:transportOrderId`}
                            element={
                                <SuspendedWithSpinner>
                                    <OrderDetailsPage comingFrom={INCOMING_ROUTE} />
                                </SuspendedWithSpinner>
                            }
                        />
                        <Route
                            path={`${OUTGOING_ROUTE}${CREATE_ROUTE}`}
                            element={
                                <SuspendedWithSpinner>
                                    <CreateOrder />
                                </SuspendedWithSpinner>
                            }
                        />
                        <Route
                            path={`${OUTGOING_ROUTE}/*`}
                            element={
                                <SuspendedWithSpinner>
                                    <OutgoingOrders />
                                </SuspendedWithSpinner>
                            }
                        />
                        <Route
                            path={`${OUTGOING_ROUTE}/:transportOrderId`}
                            element={
                                <SuspendedWithSpinner>
                                    <OrderDetailsPage comingFrom={OUTGOING_ROUTE} />
                                </SuspendedWithSpinner>
                            }
                        />
                        <Route
                            path={`${CARRIER_COMPLIANCE_ROUTE}`}
                            element={
                                <SuspendedWithSpinner>
                                    <CarrierCompliance />
                                </SuspendedWithSpinner>
                            }
                        />
                        <Route path='*' element={<DefaultRedirect />} />
                    </Routes>
                    <RouteUpdater />
                </ApplicationLayout.Body>
            </ApplicationLayout>
        </IntlProvider>
    );
};

export default App;
